import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { getFieldLabel, getFieldPath } from '../../utils/recipientRequirements';
import SearchableSelect from '../SearchableSelect';
import { InfoIcon } from '../../assets/icons';

const normalizeRegex = (regexPattern) => {
  if (!regexPattern || typeof regexPattern !== 'string') {
    return regexPattern;
  }
  let normalized = regexPattern;
  normalized = normalized.replace(/([^\\])d\{/g, '$1\\d{');
  normalized = normalized.replace(/^d\{/g, '\\d{');
  normalized = normalized.replace(/([^\\])D\{/g, '$1\\D{');
  normalized = normalized.replace(/^D\{/g, '\\D{');
  normalized = normalized.replace(/([^\\])s\{/g, '$1\\s{');
  normalized = normalized.replace(/^s\{/g, '\\s{');
  normalized = normalized.replace(/([^\\])S\{/g, '$1\\S{');
  normalized = normalized.replace(/^S\{/g, '\\S{');
  normalized = normalized.replace(/([^\\])w\{/g, '$1\\w{');
  normalized = normalized.replace(/^w\{/g, '\\w{');
  normalized = normalized.replace(/([^\\])W\{/g, '$1\\W{');
  normalized = normalized.replace(/^W\{/g, '\\W{');
  return normalized;
};

function DynamicRecipientForm({ requirements, sections, formData, onChange, errors = {}, variant = 'default' }) {
  const [tooltipField, setTooltipField] = useState(null);
  const { countries: configCountries } = useSelector((state) => state.config);
  const isWizard = variant === 'wizard';
  const fieldWrapClass = isWizard ? '' : 'space-y-2';
  const labelClass = isWizard
    ? 'mb-2 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.1em] text-[var(--text-tertiary)] font-[family-name:var(--font-ui)]'
    : 'flex items-center gap-2 text-sm font-medium text-foreground';
  const infoIconClass = isWizard
    ? 'h-3.5 w-3.5 shrink-0 cursor-help text-[var(--text-tertiary)]'
    : 'h-4 w-4 shrink-0 cursor-help text-muted-foreground';
  const hintClass = isWizard
    ? 'mt-1.5 text-[11.5px] leading-snug text-[var(--text-tertiary)] font-[family-name:var(--font-ui)]'
    : 'text-xs text-muted-foreground mt-1';
  const fieldsContainerClass = isWizard ? 'grid gap-[18px]' : 'space-y-4';
  const sectionContainerClass = isWizard ? 'grid gap-[18px]' : 'space-y-6';
  const sectionFieldsClass = isWizard ? 'grid gap-[18px]' : 'space-y-4 pl-2';
  const sectionTitleClass = isWizard
    ? 'text-[11px] font-semibold uppercase tracking-[0.1em] text-[var(--text-tertiary)] font-[family-name:var(--font-ui)]'
    : 'text-lg font-semibold text-foreground border-b border-border pb-2';

  const handleChange = (fieldKey, value) => {
    onChange(fieldKey, value);
  };

  const getFieldKey = (req) => {
    if (req.key) return req.key;
    if (req.field) return getFieldPath(req.field);
    if (req.data) return getFieldPath(req.data);
    if (req.variableName) return getFieldPath(req.variableName);
    return '';
  };

  const getFieldValue = (fieldKey) => formData[fieldKey] || '';

  const renderField = (req) => {
    const fieldKey = getFieldKey(req);
    if (!fieldKey) {
      return null;
    }

    const { example, regex, info, items, enum: enumValues, values, required } = req;
    const label = req.label || getFieldLabel(req.variableName || req.field || req.data || req.key || '');
    const value = getFieldValue(fieldKey);
    const error = errors[fieldKey];

    const hasItems = items && Array.isArray(items) && items.length > 0;
    const hasEnum = enumValues && Array.isArray(enumValues) && enumValues.length > 0;
    const hasValues = values && Array.isArray(values) && values.length > 0;
    const isSelectField = hasItems || hasEnum || hasValues;
    const isRequired = required === true;

    const placeholder = example ? `Enter ${label.toLowerCase()} (e.g., ${example})` : `Enter ${label.toLowerCase()}`;

    let formatInfo = null;
    if (regex && regex.trim() !== '') {
      if (example) {
        formatInfo = `Format: ${regex} (e.g., ${example})`;
      } else {
        formatInfo = `Format: ${regex}`;
      }
    }

    if (isSelectField) {
      let options = [];
      if (hasItems) {
        options = items
          .filter((item) => item.is_active !== false)
          .map((item) => ({
            value: item.value,
            label: item.label || item.value,
          }));
      } else if (hasEnum) {
        options = enumValues.map((option) => ({
          value: option,
          label: option,
        }));
      } else if (hasValues) {
        const seen = new Set();
        const mappedOptions = values.map((option) => {
          if (typeof option === 'object' && option !== null) {
            const value = option.data || option.value || '';
            const label = option.label || option.data || option.value || '';
            return {
              value: String(value),
              label: String(label),
            };
          }
          const stringOption = String(option);
          return {
            value: stringOption,
            label: stringOption,
          };
        });

        options = mappedOptions.filter((option) => {
          const normalizedValue = option.value.toLowerCase().trim();

          if (!normalizedValue) return false;
          if (seen.has(normalizedValue)) {
            return false;
          }
          seen.add(normalizedValue);
          return true;
        });
      }
      if (req.selected !== undefined && req.selected !== null && req.selected !== '') {
        const selectedValue = String(req.selected);
        const selectedExists = options.some((opt) => String(opt.value).toLowerCase() === selectedValue.toLowerCase());

        if (!selectedExists) {
          let selectedLabel = selectedValue;
          if (configCountries && configCountries.length > 0 && /^[A-Z]{2}$/i.test(selectedValue)) {
            const country = configCountries.find((c) => c.code && c.code.toUpperCase() === selectedValue.toUpperCase());
            if (country && country.name) {
              selectedLabel = country.name;
            }
          }

          const selectedOption = {
            value: selectedValue,
            label: selectedLabel,
          };

          options.unshift(selectedOption);
        }
      }

      const selectTriggerClassName = isWizard
        ? `flex h-auto w-full items-center gap-2 rounded-lg border bg-[var(--white)] px-3.5 py-3 text-left text-[15px] text-[var(--n900)] outline-none font-[family-name:var(--font-ui)] ${
            error ? 'border-[var(--color-error)]' : 'border-[var(--color-border)]'
          }`
        : '';

      return (
        <div key={fieldKey} className={fieldWrapClass}>
          <label htmlFor={`select-${fieldKey}`} className={labelClass}>
            <span>
              {label}
              {isRequired && <span className={isWizard ? 'text-[var(--color-error)]' : 'text-destructive ml-1'}>*</span>}
            </span>
            {info && (
              <div className='relative group'>
                <InfoIcon
                  className={infoIconClass}
                  onMouseEnter={() => setTooltipField(fieldKey)}
                  onMouseLeave={() => setTooltipField(null)}
                />
                {tooltipField === fieldKey && (
                  <div className='absolute z-10 left-0 top-full mt-1.5 w-48 rounded-md border border-border bg-popover p-2 text-xs normal-case tracking-normal text-foreground shadow-lg font-[family-name:var(--font-ui)]'>
                    {info}
                  </div>
                )}
              </div>
            )}
          </label>
          <SearchableSelect
            id={`select-${fieldKey}`}
            name={fieldKey}
            options={options}
            value={value}
            onChange={(event) => {
              const selectedValue = event?.target?.value || event;
              handleChange(fieldKey, selectedValue);
            }}
            placeholder={`Select ${label.toLowerCase()}`}
            hasError={!!error}
            triggerClassName={selectTriggerClassName}
          />
          {info && isWizard && !error && <p className={hintClass}>{info}</p>}
          {formatInfo && <p className={hintClass}>{formatInfo}</p>}
          {error && <p className={`${hintClass} text-[var(--color-error)]`}>{error}</p>}
        </div>
      );
    }

    const inputClassName = isWizard
      ? `w-full rounded-lg border border-[var(--color-border)] bg-[var(--white)] px-3.5 py-3 text-[15px] text-[var(--n900)] outline-none placeholder:text-[var(--text-tertiary)] focus:border-[var(--n600)] font-[family-name:var(--font-ui)] ${
          error ? 'border-[var(--color-error)]' : ''
        }`
      : `flex w-full px-3 py-2 text-base ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm h-10 rounded-md border border-input bg-background ${
          error ? 'border-destructive' : ''
        }`;

    return (
      <div key={fieldKey} className={fieldWrapClass}>
        <label htmlFor={fieldKey} className={labelClass}>
          <span>
            {label}
            {isRequired && <span className={isWizard ? 'text-[var(--color-error)]' : 'text-destructive ml-1'}>*</span>}
          </span>
          {info && (
            <div className='relative group'>
              <InfoIcon
                className={infoIconClass}
                onMouseEnter={() => setTooltipField(fieldKey)}
                onMouseLeave={() => setTooltipField(null)}
              />
              {tooltipField === fieldKey && (
                <div className='absolute z-10 left-0 top-full mt-1.5 w-48 rounded-md border border-border bg-popover p-2 text-xs normal-case tracking-normal text-foreground shadow-lg whitespace-normal font-[family-name:var(--font-ui)]'>
                  {info}
                </div>
              )}
            </div>
          )}
        </label>
        <input
          type='text'
          id={fieldKey}
          name={fieldKey}
          value={value}
          onChange={(e) => handleChange(fieldKey, e.target.value)}
          placeholder={placeholder}
          required={isRequired}
          pattern={regex ? normalizeRegex(regex) : undefined}
          className={inputClassName}
        />
        {info && isWizard && !error && <p className={hintClass}>{info}</p>}
        {formatInfo && <p className={hintClass}>{formatInfo}</p>}
        {error && <p className={`${hintClass} text-[var(--color-error)]`}>{error}</p>}
      </div>
    );
  };

  if (sections && Array.isArray(sections) && sections.length > 0) {
    return (
      <div className={sectionContainerClass}>
        {sections.map((section) => {
          if (!section.fields || !Array.isArray(section.fields) || section.fields.length === 0) {
            return null;
          }

          const uniqueFields = section.fields.filter((req, index, self) => {
            const fieldKey = getFieldKey(req);
            return (
              fieldKey &&
              index ===
                self.findIndex((r) => {
                  const otherKey = getFieldKey(r);
                  return otherKey === fieldKey;
                })
            );
          });

          if (uniqueFields.length === 0) {
            return null;
          }

          const sectionKey = `${section.name || 'section'}-${section.fields?.[0]?.data || section.fields?.[0]?.field || section.fields?.[0]?.variableName || 'default'}`;
          return (
            <div key={sectionKey} className={isWizard ? 'grid gap-[18px]' : 'space-y-4'}>
              {section.name ? <h3 className={sectionTitleClass}>{section.name}</h3> : null}
              <div className={sectionFieldsClass}>{uniqueFields.map((req) => renderField(req))}</div>
            </div>
          );
        })}
      </div>
    );
  }

  if (!requirements || requirements.length === 0) {
    return null;
  }

  const uniqueRequirements = requirements.filter((req, index, self) => {
    const fieldKey = req.key || (req.field ? getFieldPath(req.field) : '') || (req.variableName ? getFieldPath(req.variableName) : '');
    return (
      fieldKey &&
      index ===
        self.findIndex((r) => {
          const otherKey =
            r.key ||
            (r.field ? getFieldPath(r.field) : '') ||
            (r.data ? getFieldPath(r.data) : '') ||
            (r.variableName ? getFieldPath(r.variableName) : '');
          return otherKey === fieldKey;
        })
    );
  });

  return <div className={fieldsContainerClass}>{uniqueRequirements.map((req) => renderField(req))}</div>;
}

DynamicRecipientForm.propTypes = {
  requirements: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      key: PropTypes.string,
      field: PropTypes.string,
      variableName: PropTypes.string,
      regex: PropTypes.string,
      required: PropTypes.bool,
      example: PropTypes.string,
      info: PropTypes.string,
      developerNote: PropTypes.string,
      rails: PropTypes.arrayOf(PropTypes.string),
      accountHolderTypes: PropTypes.arrayOf(PropTypes.string),
      items: PropTypes.arrayOf(
        PropTypes.shape({
          label: PropTypes.string,
          value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
          is_active: PropTypes.bool,
        }),
      ),
      enum: PropTypes.arrayOf(PropTypes.string),
      values: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.shape({ data: PropTypes.string, label: PropTypes.string })])),
      optional: PropTypes.bool,
    }),
  ),
  sections: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      fields: PropTypes.arrayOf(
        PropTypes.shape({
          label: PropTypes.string,
          key: PropTypes.string,
          field: PropTypes.string,
          data: PropTypes.string,
          variableName: PropTypes.string,
          regex: PropTypes.string,
          required: PropTypes.bool,
          example: PropTypes.string,
          info: PropTypes.string,
          developerNote: PropTypes.string,
          values: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.shape({ data: PropTypes.string, label: PropTypes.string })])),
        }),
      ),
    }),
  ),
  formData: PropTypes.shape({}).isRequired,
  onChange: PropTypes.func.isRequired,
  errors: PropTypes.shape({}),
  variant: PropTypes.oneOf(['default', 'wizard']),
};

export default DynamicRecipientForm;
